/**
 * Quality Data Packages (module: Turnover Packs, spec §16). CR-2026-QDP (2026-10-14). Server: /api/qdp.
 * One package per build group. Build > submit > technical review by a different person > QM approval > issue.
 * Gaps come from the records: blocking gaps are fixed in the records, exceptionable gaps need a declared reason,
 * disclosed items print in Section 10. Nothing is typed into the package.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Card, Chip, Container, Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import QdpDocument from "./QdpDocument";
import { STATE_LABEL, STATE_COLOR, SEVERITY, gapRows, actionsFor, pdfFileName, zipFileName, hasPdf } from "./qdpModel";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const when = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "");

export default function QualityDataPackages() {
  const [params, setParams] = useSearchParams();
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const me = [loginUser?.decodedToken?.firstName, loginUser?.decodedToken?.lastName].filter(Boolean).join(" ") || loginUser?.decodedToken?.name || "";
  const jobId = params.get("jobId") || "";
  const pkgId = params.get("id") || "";
  const [jobs, setJobs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [list, setList] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [reasons, setReasons] = useState({});
  const [changeNote, setChangeNote] = useState("");
  const [coc, setCoc] = useState("");
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); if (k === "jobId") p.delete("id"); setParams(p); };

  useEffect(() => { ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || r.data?.data || [])).catch(() => setJobs([])); }, []);
  const loadList = useCallback(async () => {
    if (!jobId) { setList([]); setGroups([]); return; }
    try {
      const [a, b] = await Promise.all([ApiCall.get(`/qdp?jobId=${jobId}`), ApiCall.get(`/build-groups?jobId=${jobId}`)]);
      setList(a.data.data || []); setGroups((b.data.data?.groups || []).filter((g) => g.status === "ACTIVE"));
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load packages.") }); }
  }, [jobId]);
  useEffect(() => { loadList(); }, [loadList]);
  const loadPkg = useCallback(async () => {
    setPkg(null); setNote(""); setReasons({});
    if (!pkgId) return;
    try { const r = await ApiCall.get(`/qdp/${pkgId}`); setPkg(r.data.data); } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load the package.") }); }
  }, [pkgId]);
  useEffect(() => { loadPkg(); }, [loadPkg]);

  const run = async (fn, after) => {
    setBusy(true); setMsg(null);
    try { const r = await fn(); setMsg({ severity: "success", text: r?.data?.message || "Done." }); if (after) after(r); await loadList(); await loadPkg(); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not done.") }); await loadPkg(); } finally { setBusy(false); }
  };
  const build = (groupId) => run(() => ApiCall.post("/qdp/build", { groupId, changeNote: changeNote.trim() || undefined }), (r) => { setChangeNote(""); set("id", String(r.data.data.id)); });
  // CR-2026-QDP-FINAL: the rolled-up job package; closeout needs it issued (or a co-signed closeout override)
  const buildFinal = () => run(() => ApiCall.post("/qdp/build-final", { jobId: Number(jobId), changeNote: changeNote.trim() || undefined }), (r) => { setChangeNote(""); set("id", String(r.data.data.id)); });
  const finalPkg = list.filter((p) => p.scope === "FINAL" && p.state !== "WITHDRAWN")[0] || null;
  const act = (path, body) => run(() => ApiCall.post(`/qdp/${pkg.id}/${path}`, body));
  const removeEx = (eid) => run(() => ApiCall.delete(`/qdp/${pkg.id}/exceptions/${eid}`));
  // CR-2026-QDP-PDF: the issued file, produced once on the server and served unchanged (each download is on the audit log)
  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };
  const blobError = async (e, fallback) => {
    const d = e?.response?.data;
    if (d instanceof Blob) { try { const j = JSON.parse(await d.text()); return [j.message || fallback].concat(j.problems || []).join(" "); } catch (_) { return fallback; } }
    return msgOf(e, fallback);
  };
  // CR-2026-QDP-ZIP: the turnover ZIP (package PDF and every record file, each checked against its SHA-256 at issue)
  const downloadZip = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await ApiCall.get(`/qdp/${pkg.id}/zip`, { responseType: "blob", timeout: 600000 });
      saveBlob(r.data, zipFileName(pkg));
      setMsg({ severity: pkg.state === "SUPERSEDED" ? "warning" : "success",
        text: `${zipFileName(pkg)} downloaded. Record files were checked against the SHA-256 frozen at issue where one was recorded; MANIFEST.txt inside shows the result for each file.${pkg.state === "SUPERSEDED" ? " This revision is superseded: send the customer the current revision." : ""}` });
    } catch (e) {
      setMsg({ severity: "error", text: await blobError(e, "Could not download the turnover ZIP.") });
    } finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await ApiCall.get(`/qdp/${pkg.id}/pdf`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data); const link = document.createElement("a");
      link.href = url; link.download = pdfFileName(pkg); document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      const sha = r.headers?.["x-qdp-pdf-sha256"];
      setMsg({ severity: pkg.state === "SUPERSEDED" ? "warning" : "success",
        text: `${pdfFileName(pkg)} downloaded${sha ? ` (SHA-256 ${sha.slice(0, 16)})` : ""}.${pkg.state === "SUPERSEDED" ? " This revision is superseded: send the customer the current revision." : ""}` });
      await loadPkg();
    } catch (e) {
      let text = "Could not download the PDF.";
      const d = e?.response?.data;
      if (d instanceof Blob) { try { text = JSON.parse(await d.text()).message || text; } catch (_) { /* keep default */ } } else text = msgOf(e, text);
      setMsg({ severity: "error", text });
    } finally { setBusy(false); }
  };
  const a = pkg ? actionsFor(pkg, perms, me) : {};
  const gaps = pkg ? gapRows(pkg.content, pkg.exceptions) : [];
  const latestFor = (gid) => list.filter((p) => Number(p.buildGroupId) === Number(gid) && p.state !== "WITHDRAWN")[0] || null;

  return (
    <>
      <Helmet>
        <title>Quality data packages | SFM</title>
        <style>{"@media print { body * { visibility: hidden !important; } .qdp-doc, .qdp-doc * { visibility: visible !important; } .qdp-doc { position: absolute; left: 0; top: 0; width: 100%; } }"}</style>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4">Quality data packages</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One package per sequence or floor, built from SFM records. A different person does the technical review; the Quality Manager approves and issues.
        </Typography>
        <TextField select size="small" label="Job" value={jobId} onChange={(e) => set("jobId", e.target.value)} sx={{ minWidth: 300, mb: 2 }}>
          <MenuItem value="">Choose a job</MenuItem>
          {jobs.map((j) => <MenuItem key={j.id} value={String(j.id)}>{j.name}</MenuItem>)}
        </TextField>
        {msg && <Alert severity={msg.severity} sx={{ mb: 2, whiteSpace: "pre-line" }}>{msg.text}</Alert>}
        {jobId && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6">Build groups</Typography>
                {groups.length === 0 && <Typography variant="caption">No active build groups on this job. Create them on the Build Groups page first.</Typography>}
                <Table size="small">
                  <TableBody>
                    {groups.map((g) => {
                      const p = latestFor(g.id);
                      return (
                        <TableRow key={g.id}>
                          <TableCell sx={{ fontWeight: 600 }}>{g.code}</TableCell>
                          <TableCell>{g.pieces} pcs</TableCell>
                          <TableCell>{p ? <Chip size="small" clickable color={STATE_COLOR[p.state]} label={`Rev ${p.revision} ${STATE_LABEL[p.state]}`} onClick={() => set("id", String(p.id))} /> : <Typography variant="caption">No package</Typography>}</TableCell>
                          <TableCell>{perms.includes("build_qdp") && (!p || ["ISSUED"].includes(p.state)) && <Button size="small" disabled={busy || (p?.state === "ISSUED" && changeNote.trim().length < 10)} onClick={() => build(g.id)}>{p ? "New rev" : "Build"}</Button>}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle2">Final package (job closeout)</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">Rolls up every group package. The job closes only when it is issued, or under a co-signed closeout override.</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    {finalPkg ? <Chip size="small" clickable color={STATE_COLOR[finalPkg.state]} label={`Rev ${finalPkg.revision} ${STATE_LABEL[finalPkg.state]}`} onClick={() => set("id", String(finalPkg.id))} /> : <Typography variant="caption">No final package</Typography>}
                    {perms.includes("build_qdp") && (!finalPkg || finalPkg.state === "ISSUED") && <Button size="small" disabled={busy || (finalPkg?.state === "ISSUED" && changeNote.trim().length < 10)} onClick={buildFinal}>{finalPkg ? "New rev" : "Build final"}</Button>}
                  </Stack>
                </Box>
                {perms.includes("build_qdp") && list.some((p) => p.state === "ISSUED") && (
                  <TextField fullWidth size="small" multiline sx={{ mt: 1 }} label="Change note for a new revision (why it changes)" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
                )}
                <Typography variant="subtitle2" sx={{ mt: 2 }}>All revisions</Typography>
                {list.map((p) => (
                  <Box key={p.id} onClick={() => set("id", String(p.id))} sx={{ cursor: "pointer", py: 0.5, fontWeight: String(p.id) === pkgId ? 700 : 400 }}>
                    <Typography variant="body2">{p.number} Rev {p.revision} · {STATE_LABEL[p.state]}{p.issuedAt ? ` ${when(p.issuedAt)}` : ""}</Typography>
                  </Box>
                ))}
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {!pkg ? <Typography variant="body2" color="text.secondary">Choose a package, or build one for a group.</Typography> : (
                <>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                      <Typography variant="h6">{pkg.number} Rev {pkg.revision}</Typography>
                      <Chip size="small" color={STATE_COLOR[pkg.state]} label={STATE_LABEL[pkg.state]} />
                      <Typography variant="caption">Built by {pkg.builtByName}{pkg.lastBuiltByName && pkg.lastBuiltByName !== pkg.builtByName ? `, refreshed by ${pkg.lastBuiltByName}` : ""} {when(pkg.builtAt)}</Typography>
                    </Stack>
                    {pkg.state === "ISSUED" && pkg.matchesRecords === false && (
                      <Alert severity="error" sx={{ mt: 1 }}>The records changed after this package was issued. It no longer matches them{pkg.scope === "FINAL" ? ", so the job cannot close on it" : ", so the final package cannot include it"}. Build, review and issue a new revision.</Alert>
                    )}
                    {pkg.state === "ISSUED" && pkg.matchesRecords === true && <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>Still matches the records.</Typography>}
                    {pkg.returnReason && pkg.state === "DRAFT" && <Alert severity="warning" sx={{ mt: 1 }}>Returned by {pkg.returnedByName} {when(pkg.returnedAt)}: {pkg.returnReason}</Alert>}
                    {pkg.changeNote && <Typography variant="body2" sx={{ mt: 1 }}>Change note: {pkg.changeNote}</Typography>}
                    {pkg.reviewNote && <Typography variant="body2">Technical review ({pkg.reviewedByName}, {when(pkg.reviewedAt)}): {pkg.reviewNote}</Typography>}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                      {a.rebuild && <Button size="small" disabled={busy} onClick={() => act("rebuild")}>Refresh from records</Button>}
                      {a.submit && <Button size="small" variant="contained" disabled={busy} onClick={() => act("submit")}>Submit for technical review</Button>}
                      {a.issue && <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => act("issue")}>Issue (locks the package)</Button>}
                      {hasPdf(pkg) && <Button size="small" variant="outlined" disabled={busy} onClick={downloadPdf}>Download issued PDF</Button>}
                      {hasPdf(pkg) && <Button size="small" variant="outlined" disabled={busy} onClick={downloadZip}>Download turnover ZIP</Button>}
                      {["ISSUED", "SUPERSEDED", "APPROVED", "REVIEWED"].includes(pkg.state) && <Button size="small" onClick={() => window.print()}>{hasPdf(pkg) ? "Print from browser" : "Print review copy"}</Button>}
                    </Stack>
                    {hasPdf(pkg) && (
                      <Typography variant="caption" display="block" sx={{ mt: 1, wordBreak: "break-all" }}>
                        {pkg.pdf?.current ? `Issued PDF (PDF/A-2b, self-checked): SHA-256 ${pkg.pdf.current.sha256} · ${pkg.pdf.current.pages} page(s) · produced ${when(pkg.pdf.current.renderedAt)}${pkg.pdf.current.renderedByName ? ` at the first download by ${pkg.pdf.current.renderedByName}` : ""}. Send this file to the customer; every download serves the same bytes.`
                          : "The issued PDF is produced at the first download and stored; every later download serves the same file."}
                        {pkg.pdf?.files?.length > 1 ? ` Earlier file(s) of this package from a previous renderer remain on record: ${pkg.pdf.files.filter((f) => f.sha256 !== pkg.pdf.current?.sha256).map((f) => f.sha256.slice(0, 16)).join(", ")}.` : ""}
                      </Typography>
                    )}
                    {(a.review || a.withdraw) && (
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                        <TextField size="small" fullWidth label={a.review ? "Review note: what you checked, against which records (or what must change)" : "Reason to withdraw the draft"} value={note} onChange={(e) => setNote(e.target.value)} />
                        {a.review && <Button variant="contained" disabled={busy} onClick={() => act("review", { decision: "ACCEPT", note })}>Accept</Button>}
                        {a.review && <Button color="warning" disabled={busy} onClick={() => act("review", { decision: "RETURN", note })}>Return</Button>}
                        {a.withdraw && <Button color="error" disabled={busy} onClick={() => act("withdraw", { reason: note })}>Withdraw draft</Button>}
                      </Stack>
                    )}
                    {a.approve && (
                      <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <TextField size="small" multiline minRows={2} label="Certificate wording (leave blank for the standard counsel-reviewed text)" value={coc} onChange={(e) => setCoc(e.target.value)} />
                        <Button variant="contained" disabled={busy} onClick={() => act("approve", coc.trim() ? { cocText: coc.trim() } : {})}>Approve as Quality Manager</Button>
                      </Stack>
                    )}
                    {pkg.state === "ISSUED" && pkg.verifyToken && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Verify link: /qdp/verify/{pkg.verifyToken} · fingerprint {pkg.packageSha256}</Typography>}
                  </Card>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6">Gaps ({gaps.filter((g) => g.open).length} open of {gaps.length})</Typography>
                    {gaps.length === 0 && <Typography variant="caption">No gaps.</Typography>}
                    <SmartTable tableId="qdp-gaps" lockedColumns={["Section", "Gap"]} size="small">
                      <TableHead><TableRow><TableCell>Section</TableCell><TableCell>Gap</TableCell><TableCell>Where</TableCell><TableCell>Answer</TableCell></TableRow></TableHead>
                      <TableBody>
                        {gaps.map((g) => (
                          <TableRow key={g.key}>
                            <TableCell>{g.section}</TableCell>
                            <TableCell><Chip size="small" color={SEVERITY[g.severity].color} label={SEVERITY[g.severity].label} sx={{ mr: 1 }} />{g.text}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>{g.where || ""}</TableCell>
                            <TableCell sx={{ minWidth: 240 }}>
                              {g.exception ? (
                                <>
                                  <Typography variant="caption" display="block">Exception: {g.exception.reason} ({g.exception.createdByName})</Typography>
                                  {a.declare && <Button size="small" color="error" disabled={busy} onClick={() => removeEx(g.exception.id)}>Remove</Button>}
                                </>
                              ) : g.severity === "EXCEPTIONABLE" && a.declare ? (
                                <Stack direction="row" spacing={0.5}>
                                  <TextField size="small" placeholder="Reason (10+ characters)" value={reasons[g.key] || ""} onChange={(e) => setReasons((r) => ({ ...r, [g.key]: e.target.value }))} />
                                  <Button size="small" disabled={busy} onClick={() => act("exceptions", { gapKey: g.key, reason: reasons[g.key] || "" })}>Declare</Button>
                                </Stack>
                              ) : <Typography variant="caption" color="text.secondary">{SEVERITY[g.severity].help}</Typography>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </SmartTable>
                  </Card>
                  <Card sx={{ p: 0, overflow: "auto" }}><QdpDocument pkg={pkg} /></Card>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
}
